import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { isSmartlingSystemTest, isSystemTest } from '@/core/utilities';
import type { TOptions } from 'i18next';
import type { TooltipPosition } from '@seeqdev/qomponents';

export const DEFAULT_TOOL_TIP_DELAY = 500;

export interface HoverTooltipProps {
  placement?: TooltipPosition;
  delay?: number;
  text?: string;
  formattedText?: string;
  translateParams?: TOptions;
  skipMemo?: boolean;
  children?: React.JSX.Element;
}

/**
 * Generic tooltip that appears after hover delay
 */
const HoverTooltipUnwrapped = ({
  text = '',
  delay = DEFAULT_TOOL_TIP_DELAY,
  placement,
  children = <></>,
  formattedText,
  translateParams,
}: HoverTooltipProps) => {
  const { t } = useTranslation();

  if (_.isEmpty(text) && !formattedText) {
    return <>{children}</>;
  }

  if (isSystemTest()) {
    if (isSmartlingSystemTest()) {
      // For the Smartling system test, enable tooltips, so we can take screenshots of them
      return (
        <span
          data-qtip-placement={placement}
          data-qtip-text={formattedText ?? t(text, translateParams ?? {})}
          data-qtip-delay={delay}
          data-qtip-is-html={!!formattedText}
          data-testid="hover-tooltip">
          {children}
        </span>
      );
    } else {
      // For all other system tests, disable tooltips entirely, so they don't cover buttons and cause flakiness
      return <>{children}</>;
    }
  } else {
    // For production environments, enable tooltips
    return (
      <span
        data-qtip-placement={placement}
        data-qtip-text={formattedText ?? t(text, translateParams ?? {})}
        data-qtip-delay={delay}
        data-testid="hover-tooltip"
        data-qtip-is-html={!!formattedText}>
        {children}
      </span>
    );
  }
};

export const HoverTooltip = React.memo(
  HoverTooltipUnwrapped,
  (prev, next) =>
    !(
      !_.isEqual(prev.translateParams, next.translateParams) ||
      !_.isEqual(prev.formattedText, next.formattedText) ||
      !_.isEqual(prev.children, next.children) ||
      prev.placement !== next.placement ||
      prev.delay !== next.delay ||
      prev.text !== next.text ||
      prev.skipMemo
    ),
);
